import * as Yup from 'yup';
import FormUIOptions from '../formElements/formUIOptions';
import { isConditionLocal } from './filterSchema';

const GENERIC_ERROR_MESSAGE = 'This field is required';
const PHONE_ERROR_MESSAGE = 'Please enter a valid phone number';
const EMAIL_ERROR_MESSAGE = 'Please enter a valid email address';

export function hasRequiredFields(fields) {
  return fields.some((field) => !!field.required);
}

function getValidatedFields(fields) {
  const validatedFields = [];

  fields.forEach((field) => {
    switch (field.type) {
      case FormUIOptions.Table:
      case FormUIOptions.BeforeAfterTableInline:
      case FormUIOptions.BeforeAfterTableModal:
        const requiredSubfields = field.tableSchema.filter(
          (tableField) => tableField.required
        );
        if (requiredSubfields.length > 0) {
          validatedFields.push({
            ...field,
            tableSchema: requiredSubfields,
          });
        } else {
          console.warn(
            `Field ${field.name} must have at least one required input`
          );
        }

      default:
        return (
          (field.required ||
            field.type === FormUIOptions.Telephone ||
            field.type === FormUIOptions.Email) &&
          validatedFields.push(field)
        );
    }
  });

  return validatedFields;
}

//this function (below) adds conditions for form elements to the validation schema
//for now this should work only for local fields (within the same formik form/ schema)
const addConditions = (yupField, conditions) => {
  if (conditions && isConditionLocal(conditions)) {
    if (!Array.isArray(conditions)) {
      conditions = [conditions];
    }
    const conditionFields = conditions.map((condition) => condition.field);
    const valueFields = conditions.map((condition) => condition.value);

    return yupField.when(conditionFields, {
      is: (...fields) =>
        valueFields.every((value, index) => fields[index] === value),
      then: yupField.required(GENERIC_ERROR_MESSAGE),
      otherwise: yupField.notRequired(),
    });
  }
  return yupField.required(GENERIC_ERROR_MESSAGE);
};

function convertTypesForValidation(
  fieldType,
  tableSchema = [],
  conditions,
  required
) {
  const phoneCondition = Yup.string().matches(
    /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/g,
    PHONE_ERROR_MESSAGE
  );

  const emailCondition = Yup.string().email(EMAIL_ERROR_MESSAGE);

  switch (fieldType) {
    case FormUIOptions.Text:
    case FormUIOptions.TextArea:
    case FormUIOptions.Place:
    case FormUIOptions.Money:
    case FormUIOptions.Number:
    case FormUIOptions.Select:
    case FormUIOptions.Radio:
    case FormUIOptions.RadioBigGroup:
      return addConditions(Yup.string(), conditions);

    case FormUIOptions.YesNo:
      return addConditions(Yup.bool(), conditions);

    case FormUIOptions.Telephone:
      return required
        ? addConditions(phoneCondition, conditions)
        : phoneCondition;

    case FormUIOptions.Email:
      return required
        ? addConditions(emailCondition, conditions)
        : emailCondition;

    case FormUIOptions.UnitMoney:
      return addConditions(
        Yup.object()
          .shape({
            value: Yup.number(),
            unit: Yup.string(),
          })
          .nullable(),
        conditions
      );

    case FormUIOptions.Table:
    case FormUIOptions.BeforeAfterTableInline:
    case FormUIOptions.BeforeAfterTableModal:
      const arrayItemSchema = tableSchema.reduce((obj, tableField) => {
        const tableFieldType = convertTypesForValidation(tableField.type);
        return {
          ...obj,
          [tableField.name]: tableFieldType,
        };
      }, {});

      return Yup.array().of(Yup.object().shape(arrayItemSchema));

    case '':
      return console.error('empty arg in type remapping');

    default:
      return console.error(
        `Failed to convert validation type: no matching Yup types found`
      );
  }
}

function makeValidationProperties(fields) {
  const properties = {};

  fields.forEach((field) => {
    const type = convertTypesForValidation(
      field.type,
      field.tableSchema,
      field.condition,
      field.required
    );
    properties[field.name] = type;
  });
  return Yup.object(properties);
}

export function getValidationSchema(fields) {
  const validatedFields = getValidatedFields(fields);
  try {
    const validationSchema = makeValidationProperties(validatedFields);
    return validationSchema;
  } catch (err) {
    console.error('Failed to create validation schema: ', err);
    return {};
  }
}
